import { createContext, useContext, useState, useEffect } from 'react'
import { IS_DEMO } from '../lib/demoClient'
import { supabase } from '../lib/supabase'
import { getSessionWithTimeout } from '../lib/authSession'
import { raceTimeout } from '../lib/syncEngine'

const PROFILE_CACHE_KEY = 'chatrone.auth.profile'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  // Hydrate the last-known profile on first render so role-gated UI paints
  // instantly instead of blanking while the network profile load is in flight.
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PROFILE_CACHE_KEY) || 'null') } catch { return null }
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // NOTE: no run-once guard here. Under StrictMode's dev double-mount, a
    // ref guard left the auth listener permanently unsubscribed (mount 1
    // subscribes → cleanup unsubscribes → mount 2 early-returns), so a live
    // sign-in stored the session but SIGNED_IN reached nobody and the login
    // page never advanced. The effect is idempotent — running it twice is fine.

    // Safety timeout — if everything hangs, stop blocking render after 5s.
    const safetyTimer = setTimeout(() => {
      setLoading(false)
    }, 5000)

    // Load the profile WITHOUT blocking the app's loading gate. The old code
    // awaited this before setLoading(false) — so a slow profile query (up to
    // the 30s REST timeout) kept the whole UI on a spinner, and the 5s safety
    // timer would flip to the LOGIN screen even though the user was signed in.
    // Now: set user immediately from the session, fetch profile in the
    // background (timeout-guarded), and cache it for instant paint next time.
    const loadProfile = async (userId) => {
      try {
        const { data: profileData } = await raceTimeout(
          supabase.from('user_profiles').select('*').eq('id', userId).single(),
          8000, 'profile',
        )
        if (profileData) {
          setProfile(profileData)
          try { localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profileData)) } catch { /* quota */ }
        }
      } catch (e) {
        console.warn('[auth] profile load failed (using cached/none):', e?.message)
      }
    }

    // 1. Check for existing session — timeout-guarded. The unguarded
    //    getSession() was the wedge: a stalled auth gate kept loading=true
    //    until the safety timer flipped to login while actually signed in.
    getSessionWithTimeout(5000).then(({ data }) => {
      const session = data?.session
      if (session?.user) {
        setUser(session.user)
        loadProfile(session.user.id) // fire-and-forget — never blocks loading
      }
      clearTimeout(safetyTimer)
      setLoading(false)
    }).catch(() => {
      clearTimeout(safetyTimer)
      setLoading(false)
    })

    // 2. Listen for changes (login, logout, refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user)
          loadProfile(session.user.id) // background — don't block
          setLoading(false)
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          setUser(session.user)
        } else if (event === 'SIGNED_OUT') {
          setUser(null)
          setProfile(null)
          try { localStorage.removeItem(PROFILE_CACHE_KEY) } catch { /* ignore */ }
          setLoading(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    // Don't rely solely on the SIGNED_IN event to unblock the login screen.
    if (data?.user) setUser(data.user)
    return data
  }

  async function signOut() {
    // The trailer: every visitor is the same principal, so a global sign-out
    // would end every open demo tab. Sign out this browser only.
    try { await supabase.auth.signOut(IS_DEMO ? { scope: 'local' } : undefined) } catch {}
    setUser(null)
    setProfile(null)
  }

  async function resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) throw error
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signOut, resetPassword, isAdmin: profile?.role === 'admin' }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}
